import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

// Interfaces
import { IWarehouse } from '../../../shared/interfaces';
import { OptionInterface } from '../../../shared/interfaces';

// Services
import { LocationWarehouseApiService } from '../../../shared/services/api/location/location-warehouse-api.service';

@Component({
  selector: 'lum-filter-locations',
  templateUrl: './filter-locations.component.html',
  styleUrls: ['./filter-locations.component.scss'],
})
export class FilterLocationsComponent implements OnInit, OnDestroy {
  @Input()
  queryId :string = 'location';

  @Input()
  value :number[] = [];

  @Input()
  label :string = 'Location';

  @Input()
  placeholder :string = '';

  @Input()
  multiple :boolean = false;

  @Input()
  inlineChips :boolean = false;

  @Output()
  onFilterSelect :EventEmitter<any[] | any> = new EventEmitter<any>();

  @ViewChild('locationInput')
  locationInput :ElementRef<HTMLInputElement>;

  locations :OptionInterface[];
  locations$ :Observable<IWarehouse[]>;
  selectedLocations :OptionInterface[] = [];
  filteredLocations :OptionInterface[] = [];
  separatorKeysCodes :number[] = [ENTER, COMMA];

  locationControl = new UntypedFormControl();

  private onDestroy$ = new Subject();

  constructor(private locationWarehouseApiService :LocationWarehouseApiService) {}

  ngOnInit() :void {
    this.initActions();
    this.getFromService();
    this.getItemsFromObservable().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onAdd(location :OptionInterface) :void {
    this.selectedLocations.push(location);
    this.locationInput.nativeElement.value = '';
    this.locationControl.setValue(null);

    this.filteredLocations = this.locations;
    this.onFilterSelect.emit({
      [this.queryId]: this.selectedLocations.map(
        (location :OptionInterface) => location.value,
      ),
    });
  }

  onRemove(item :OptionInterface) :void {
    this.selectedLocations = this.selectedLocations.filter(
      (location :OptionInterface) => location.value !== item.value,
    );
    this.onFilterSelect.emit({
      [this.queryId]: this.selectedLocations.map(
        (location :OptionInterface) => location.value,
      ),
    });
  }

  private getFromService() :void {
    this.locations$ = this.locationWarehouseApiService.warehouses$;
  }

  private initActions() :void {
    this.locationWarehouseApiService.fetchWarehouses({
      all: '1',
      parent_id: '0',
    });
  }

  private getItemsFromObservable() :Observable<IWarehouse[]> {
    return this.locations$.pipe(
      tap((locations :IWarehouse[]) => {
        const options = locations.map((location :IWarehouse) => {
          return {
            label: location.location_name,
            value: location.id,
          };
        });
        this.locations = options;
        this.filteredLocations = options;

        if (this.value?.length) {
          this.selectedLocations = options.filter((location :OptionInterface) =>
            this.value.includes(location.value as number),
          );
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get uniqueFilteredLocations() :OptionInterface[] {
    return this.filteredLocations.filter(
      (location :OptionInterface) =>
        !this.selectedLocations.find(
          (selectedLocation :OptionInterface) =>
            selectedLocation.value === location.value,
        ),
    );
  }
}
